import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, Compass, LayoutGrid, List, Plus, Users } from 'lucide-react';
import { Button, Skeleton } from '@/components/ui';
import { useAuth } from '@/app/providers';
import { useAuthModal } from '@/features/auth/authModalStore';
import { ROUTES } from '@/config/constants';
import { cn } from '@/lib/utils';
import { useMyCommunities } from '../hooks/useMyCommunities';
import { CreateCommunityModal } from '../components/CreateCommunityModal';
import { RecentlyViewedRail } from './RecentlyViewedRail';
import { MyCommunityCard } from './MyCommunityCard';
import { CommunitiesHeader } from './CommunitiesHeader';
import { useMyCommunitiesUi } from './myCommunitiesStore';
import type { MyCommunity } from '../api/myCommunities';

type FilterId = 'all' | 'owned' | 'joined' | 'private' | 'invites';
type SortId = 'active' | 'newest' | 'name' | 'members';
type ViewMode = 'list' | 'grid';

const FILTERS: { id: FilterId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'owned', label: 'Owned' },
  { id: 'joined', label: 'Joined' },
  { id: 'private', label: 'Private' },
  { id: 'invites', label: 'Invites' },
];

const SORTS: { id: SortId; label: string }[] = [
  { id: 'active', label: 'Recently active' },
  { id: 'newest', label: 'Newest' },
  { id: 'name', label: 'Name (A–Z)' },
  { id: 'members', label: 'Most members' },
];

const FILTER_TAB_BASE = cn(
  'group relative inline-flex min-w-max cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap px-3',
  'border-none bg-transparent pb-3 pt-2 text-sm font-bold transition-all duration-300',
  '[transition-timing-function:cubic-bezier(0.16,1,0.3,1)]',
  "after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:rounded-t-[2px] after:content-['']",
  'after:bg-accent after:shadow-[0_0_10px_rgba(255,255,255,0.15)]',
  'after:transition-transform after:duration-[400ms] after:[transition-timing-function:cubic-bezier(0.16,1,0.3,1)]',
);

const matchesFilter = (item: MyCommunity, filter: FilterId): boolean => {
  switch (filter) {
    case 'owned':
      return item.relationship === 'owner';
    case 'joined':
      return item.relationship === 'member';
    case 'private':
      return item.isPrivate;
    case 'invites':
      return false; // No invites system yet — placeholder tab.
    default:
      return true;
  }
};

const matchesSearch = (item: MyCommunity, tokens: string[]): boolean => {
  if (!tokens.length) return true;
  const hay = [item.community.name, item.community.description, item.community.tagline]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return tokens.every((t) => hay.includes(t));
};

const sortItems = (items: MyCommunity[], sort: SortId): MyCommunity[] => {
  const arr = [...items];
  switch (sort) {
    case 'newest':
      return arr.sort(
        (a, b) =>
          new Date(b.community.created_at).getTime() - new Date(a.community.created_at).getTime(),
      );
    case 'name':
      return arr.sort((a, b) => a.community.name.localeCompare(b.community.name));
    case 'members':
      return arr.sort((a, b) => (b.community.member_seq || 0) - (a.community.member_seq || 0));
    default:
      return arr.sort(
        (a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime(),
      );
  }
};

/**
 * My Communities — the sidebar "Communities" surface. Shows what the user owns or
 * has joined (incl. private spaces) with latest activity + announcements, a "Jump
 * back in" rail, search / sort / view controls, and per-community management.
 */
export function MyCommunitiesPage() {
  const navigate = useNavigate();
  const { profile, loading: authLoading } = useAuth();
  const openAuth = useAuthModal((s) => s.openAuth);
  const { data, isLoading, isError, refetch } = useMyCommunities();

  const search = useMyCommunitiesUi((s) => s.search);
  const [filter, setFilter] = useState<FilterId>('all');
  const [sort, setSort] = useState<SortId>('active');
  const [view, setView] = useState<ViewMode>('grid');
  const [createOpen, setCreateOpen] = useState(false);

  const items = useMemo(() => data ?? [], [data]);

  const searched = useMemo(() => {
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return items.filter((i) => matchesSearch(i, tokens));
  }, [items, search]);

  const counts = useMemo(
    () => ({
      all: searched.length,
      owned: searched.filter((i) => i.relationship === 'owner').length,
      joined: searched.filter((i) => i.relationship === 'member').length,
      private: searched.filter((i) => i.isPrivate).length,
      invites: 0,
    }),
    [searched],
  );

  const visible = useMemo(
    () => sortItems(searched.filter((i) => matchesFilter(i, filter)), sort),
    [searched, filter, sort],
  );

  const signedIn = Boolean(profile);
  const hasCommunities = items.length > 0;

  return (
    <div className="w-full max-w-[1480px]">
      <PageHeading />

      <Content
        authLoading={authLoading}
        signedIn={signedIn}
        isLoading={isLoading}
        isError={isError}
        hasCommunities={hasCommunities}
        visible={visible}
        filter={filter}
        counts={counts}
        sort={sort}
        view={view}
        onFilter={setFilter}
        onSort={setSort}
        onView={setView}
        onSignIn={openAuth}
        onExplore={() => navigate(ROUTES.home)}
        onCreate={() => setCreateOpen(true)}
        onRetry={() => void refetch()}
      />

      <CreateCommunityModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function PageHeading() {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="m-0 text-[30px] font-black tracking-[-0.8px] text-white sm:text-[34px]">
          Your Communities
        </h1>
        <p className="mt-1 text-[14px] font-semibold text-lo">
          Spaces you own, joined, or return to often.
        </p>
      </div>
      <CommunitiesHeader className="sm:hidden" />
    </div>
  );
}

interface ContentProps {
  authLoading: boolean;
  signedIn: boolean;
  isLoading: boolean;
  isError: boolean;
  hasCommunities: boolean;
  visible: MyCommunity[];
  filter: FilterId;
  counts: Record<FilterId, number>;
  sort: SortId;
  view: ViewMode;
  onFilter: (f: FilterId) => void;
  onSort: (s: SortId) => void;
  onView: (v: ViewMode) => void;
  onSignIn: () => void;
  onExplore: () => void;
  onCreate: () => void;
  onRetry: () => void;
}

function Content(props: ContentProps) {
  const {
    authLoading,
    signedIn,
    isLoading,
    isError,
    hasCommunities,
    visible,
    filter,
    counts,
    sort,
    view,
    onFilter,
    onSort,
    onView,
    onSignIn,
    onExplore,
    onCreate,
    onRetry,
  } = props;

  if (!authLoading && !signedIn) {
    return (
      <EmptyState
        icon={<Users size={30} />}
        title="Sign in to see your communities"
        body="Log in to view the spaces you've joined and the ones you run — with their latest activity and announcements."
        action={<Button onClick={onSignIn}>Sign in</Button>}
      />
    );
  }

  if (isLoading || authLoading) {
    return (
      <div className="grid grid-cols-1 auto-rows-fr gap-x-6 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-[280px] w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={<Users size={30} />}
        title="Couldn't load your communities"
        body="Something went wrong reaching the server. Give it another try."
        action={
          <Button variant="outline" onClick={onRetry}>
            Try again
          </Button>
        }
      />
    );
  }

  if (!hasCommunities) {
    return (
      <EmptyState
        icon={<Compass size={30} />}
        title="You haven't joined any communities yet"
        body="Explore the directory to find your people, or launch your own community in minutes."
        action={
          <div className="flex flex-wrap justify-center gap-2.5">
            <Button onClick={onExplore} className="gap-1.5">
              <Compass size={15} /> Explore communities
            </Button>
            <Button variant="outline" onClick={onCreate} className="gap-1.5">
              <Plus size={15} /> Create one
            </Button>
          </div>
        }
      />
    );
  }

  return (
    <>
      <RecentlyViewedRail onCreate={onCreate} />

      {/* Filter tabs + sort + view */}
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="w-full min-w-0 border-b border-white/[0.05] lg:max-w-[680px]">
          <div className="flex w-full min-w-max gap-4 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => onFilter(f.id)}
                className={cn(
                  FILTER_TAB_BASE,
                  filter === f.id
                    ? 'text-accent after:scale-x-100'
                    : 'text-lo after:scale-x-0 hover:text-hi',
                )}
              >
                {f.label}
                <span
                  className={cn(
                    'text-[10px] font-extrabold tabular-nums transition-colors duration-300',
                    filter === f.id ? 'text-accent' : 'text-lo',
                  )}
                >
                  {counts[f.id]}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-none items-center justify-end gap-2">
          <SortMenu value={sort} onChange={onSort} />
          <ViewToggle value={view} onChange={onView} />
        </div>
      </div>

      {visible.length ? (
        <div
          className={cn(
            'grid auto-rows-fr gap-x-6',
            view === 'grid'
              ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5'
              : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5',
          )}
        >
          {visible.map((item) => (
            <MyCommunityCard key={item.community.id} item={item} />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-card-line px-5 py-12 text-center text-sm text-lo">
          Nothing in <span className="font-semibold text-mid">{filter}</span> yet.
        </div>
      )}
    </>
  );
}

function SortMenu({ value, onChange }: { value: SortId; onChange: (s: SortId) => void }) {
  const [open, setOpen] = useState(false);
  const activeLabel = SORTS.find((s) => s.id === value)?.label ?? 'Recently active';

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex h-10 items-center gap-2 px-2 text-[13px] font-semibold text-mid transition-colors hover:text-hi"
      >
        {activeLabel}
        <ChevronDown size={15} className={cn('transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            role="menu"
            className="absolute right-0 z-50 mt-2 w-52 overflow-hidden rounded-lg border border-card-line bg-elev p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-2xl"
          >
            {SORTS.map((s) => (
              <button
                key={s.id}
                role="menuitemradio"
                aria-checked={s.id === value}
                onClick={() => {
                  onChange(s.id);
                  setOpen(false);
                }}
                className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-[13px] font-semibold text-mid transition-colors hover:bg-white/5 hover:text-hi"
              >
                {s.label}
                {s.id === value && <Check size={15} className="text-accent" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const btn = (mode: ViewMode, label: string, icon: React.ReactNode) => (
    <button
      onClick={() => onChange(mode)}
      aria-label={label}
      aria-pressed={value === mode}
      className={cn(
        'grid h-8 w-8 place-items-center rounded-pill transition-colors',
        value === mode ? 'bg-white/10 text-hi' : 'text-mid hover:text-hi',
      )}
    >
      {icon}
    </button>
  );

  return (
    <div className="inline-flex items-center gap-1 rounded-pill border border-card-line bg-white/[0.03] p-1">
      {btn('list', 'List view', <List size={16} />)}
      {btn('grid', 'Grid view', <LayoutGrid size={16} />)}
    </div>
  );
}

function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex max-w-[460px] flex-col items-center gap-2.5 rounded-lg border border-card-line bg-white/[0.03] px-6 py-14 text-center backdrop-blur-xl">
      <div className="mb-1 flex h-14 w-14 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.04] text-mid">
        {icon}
      </div>
      <h3 className="m-0 text-lg font-extrabold tracking-[-0.3px] text-white">{title}</h3>
      <p className="mb-2 text-[13.5px] leading-[1.55] text-mid">{body}</p>
      {action}
    </div>
  );
}
